import React from 'react';

import { 
  ArrowLeft, 
  FileText, 
  ShieldCheck, 
  DollarSign, 
  Database, 
  Globe, 
  Lock, 
  Cpu, 
  ShoppingCart, 
  Award, 
  Scale, 
  Clock,
  PhoneCall
} from 'lucide-react';

interface TermsAndConditionsProps {
  onBack: () => void;
}

interface SectionItem {
  id: string;
  title: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

export const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({ onBack }) => {
  const sections: SectionItem[] = [
    {
      id: "payment-fees",
      title: "Payment, Basic Hourly Service Rates, Remote Support, Hardware Drop-Off & Network Engineering",
      icon: <DollarSign className="w-5 h-5 text-brand-400" />,
      content: (
        <div className="space-y-4">
          <p className="leading-relaxed">
            Payment is due upon receipt of services or delivery of equipment. We accept cash, checks (please make checks payable to <strong className="text-white">Randon Brown, LLC</strong>), and major credit or debit cards processed securely via <strong className="text-white">Zoho Payments</strong>. All credit and debit card transactions processed through Zoho Payments are subject to a card processing service charge. Returned checks are subject to a $25 service charge plus any bank-assessed returned-item fees, and the outstanding balance must be settled immediately via cash, credit card, or electronic payment. Diagnostic fees cover the time and labor required to test and evaluate equipment; they apply regardless of whether the client elects to proceed with further repairs. Repair work proceeds only upon client authorization (verbal, electronic, or written).
          </p>

          {/* Official Services & Pricing Matrix Schedule */}
          <div className="pt-2 space-y-3">
            <div className="flex items-center justify-between border-b border-neutral-900 pb-2">
              <span className="text-xs font-mono font-bold uppercase tracking-wider text-brand-400">
                Official Hourly Rates &amp; Pricing Matrix Schedule
              </span>
              <span className="text-[11px] font-mono text-slate-500">
                Standard Rates
              </span>
            </div>

            {/* Pricing Matrix Table */}
            <div className="overflow-x-auto rounded-xl border border-neutral-800 bg-neutral-950/60 print:border-neutral-300">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-neutral-800 bg-neutral-900/80 text-slate-400 font-mono text-[11px]">
                    <th className="py-2.5 px-3 font-semibold">Service Tier</th>
                    <th className="py-2.5 px-3 font-semibold">Rate</th>
                    <th className="py-2.5 px-3 font-semibold">Billing Minimum &amp; Increments</th>
                    <th className="py-2.5 px-3 font-semibold">Coverage &amp; Travel Policy</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-900 text-slate-300">
                  <tr className="hover:bg-neutral-900/40">
                    <td className="py-2.5 px-3 font-medium text-white">
                      <div className="font-bold">Remote Support</div>
                      <div className="text-[11px] text-slate-400">Screen Share Diagnostics</div>
                    </td>
                    <td className="py-2.5 px-3 font-mono font-bold text-brand-400 whitespace-nowrap">$37.50 / 30m</td>
                    <td className="py-2.5 px-3">30-min minimum ($37.50), then $37.50 per 30m thereafter (rounded up)</td>
                    <td className="py-2.5 px-3">Anywhere with internet — zero travel fee</td>
                  </tr>
                  <tr className="hover:bg-neutral-900/40">
                    <td className="py-2.5 px-3 font-medium text-white">
                      <div className="font-bold">In-Shop / Bench Service</div>
                      <div className="text-[11px] text-slate-400">Device Drop-Off</div>
                    </td>
                    <td className="py-2.5 px-3 font-mono font-bold text-brand-400 whitespace-nowrap">$75 / hour</td>
                    <td className="py-2.5 px-3">1-hr minimum ($75), then $37.50 per 30m thereafter (rounded up)</td>
                    <td className="py-2.5 px-3">Open to all clients dropping off hardware — zero travel fee</td>
                  </tr>
                  <tr className="hover:bg-neutral-900/40">
                    <td className="py-2.5 px-3 font-medium text-white">
                      <div className="font-bold">Hometown Rate</div>
                      <div className="text-[11px] text-slate-400">Licking &amp; 10-Mile Radius</div>
                    </td>
                    <td className="py-2.5 px-3 font-mono font-bold text-brand-400 whitespace-nowrap">$75 / hour</td>
                    <td className="py-2.5 px-3">1-hr minimum ($75), then $37.50 per 30m thereafter (rounded up)</td>
                    <td className="py-2.5 px-3">Licking city limits (65542) &amp; within 10-mile radius (travel baked in)</td>
                  </tr>
                  <tr className="hover:bg-neutral-900/40">
                    <td className="py-2.5 px-3 font-medium text-white">
                      <div className="font-bold">Texas County On-Site</div>
                      <div className="text-[11px] text-slate-400">County Route</div>
                    </td>
                    <td className="py-2.5 px-3 font-mono font-bold text-brand-400 whitespace-nowrap">$100 / hour</td>
                    <td className="py-2.5 px-3">1-hr minimum ($100), then $50.00 per 30m thereafter (rounded up)</td>
                    <td className="py-2.5 px-3">Houston, Cabool, Raymondville, Summersville, Plato &amp; townships (travel baked in)</td>
                  </tr>
                  <tr className="hover:bg-neutral-900/40">
                    <td className="py-2.5 px-3 font-medium text-white">
                      <div className="font-bold">Regional Route On-Site</div>
                      <div className="text-[11px] text-slate-400">Regional Highway Travel</div>
                    </td>
                    <td className="py-2.5 px-3 font-mono font-bold text-brand-400 whitespace-nowrap">$150 / hour</td>
                    <td className="py-2.5 px-3">1-hr minimum ($150), then $75.00 per 30m thereafter (rounded up)</td>
                    <td className="py-2.5 px-3">Rolla, Salem, Mountain Grove, Willow Springs, Waynesville (travel baked in)</td>
                  </tr>
                  <tr className="hover:bg-neutral-900/40">
                    <td className="py-2.5 px-3 font-medium text-white">
                      <div className="font-bold">Network Engineering</div>
                      <div className="text-[11px] text-slate-400">Infrastructure &amp; Enterprise</div>
                    </td>
                    <td className="py-2.5 px-3 font-mono font-bold text-brand-400 whitespace-nowrap">$200 / hour</td>
                    <td className="py-2.5 px-3">1-hr minimum ($200), then $100.00 per 30m thereafter (rounded up)</td>
                    <td className="py-2.5 px-3">Commercial servers, VLANs, rack wiring, Starlink &amp; NVR cameras</td>
                  </tr>
                  <tr className="hover:bg-neutral-900/40">
                    <td className="py-2.5 px-3 font-medium text-white">
                      <div className="font-bold">Website Builds</div>
                      <div className="text-[11px] text-slate-400">Custom Web Development</div>
                    </td>
                    <td className="py-2.5 px-3 font-mono font-bold text-brand-400 whitespace-nowrap">From $2,000</td>
                    <td className="py-2.5 px-3">Project Scope (50% kickoff deposit, 50% upon final live launch approval)</td>
                    <td className="py-2.5 px-3">100% client direct ownership of domain registrars &amp; hosting accounts</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Travel, Minimum & Payment Summary Notice */}
            <div className="p-3 rounded-xl bg-neutral-900/60 border border-neutral-800 text-[11px] text-slate-400 leading-relaxed space-y-1">
              <p>
                <strong className="text-white font-mono">Travel, Incremental Billing &amp; Payment Methods:</strong> All on-site hourly rates bundle normal transit and round-trip travel time directly into the quoted rate with zero surprise mileage fees. Standard service calls carry a 1-hour minimum charge (or 30-min minimum for remote desktop), and are billed in 30-minute increments thereafter, rounded up to the nearest 30-minute block. We accept cash, check, and major credit/debit cards via Zoho Payments (credit card payments are subject to a card processing service charge).
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "estimates-sticker-shock",
      title: "Job Time Estimates & 'No Sticker Shock' Guarantee",
      icon: <Clock className="w-5 h-5 text-brand-400" />,
      content: `To ensure complete transparency and eliminate unexpected surprises, clients are provided with upfront time estimates based on typical repair durations prior to commencing work. All estimates reflect our 1-hour minimum and billing in 30-minute increments. In all cases, if service or diagnostics reveal that a job will require more time than the initial estimate or pre-authorized budget, work is paused immediately and the client will be contacted for direct authorization before any additional billable time is incurred. You will never receive an unexpected bill or unauthorized overtime charges.`
    },
    {
      id: "phone-consultations",
      title: "Phone Consultations and Verbal Authorizations",
      icon: <PhoneCall className="w-5 h-5 text-brand-400" />,
      content: `Communications conducted via telephone, voice call, or remote conferencing regarding project scope, troubleshooting, or repairs are subject to our standard hourly billing rates and 30-minute minimum increments. Verbal authorizations or approvals provided by the client during phone calls to proceed with additional diagnostics, extended labor, or scope changes are deemed binding under the terms of this agreement and will be documented for client records.`
    },
    {
      id: "data-backup",
      title: "Data & Backup Responsibility",
      icon: <Database className="w-5 h-5 text-brand-400" />,
      content: (
        <div className="space-y-3">
          <p>
            We take data backups seriously and maintain redundant backups on various platforms during active development. However, Randon Brown, LLC does not guarantee 100% protection against unforeseen data loss, corruption, or third-party service failures.
          </p>
          <p>
            Prior to any major diagnostic, update, or system migration, clients are strictly required to maintain current, verified backups of all personal and business data. Randon Brown, LLC shall not be held liable for any loss of data, revenue, or business interruption arising from the client’s failure to maintain independent backups or from actions taken by third-party hosting providers and plugins.
          </p>
          <div className="pt-1 space-y-1">
            <strong className="text-white font-semibold block">Websites</strong>
            <p>
              Upon project completion, a complete copy of the website/data will be delivered to the client. Once delivered and signed off, ongoing maintenance, security updates, and daily data backup responsibilities transfer entirely to the client, unless a separate ongoing maintenance agreement is in place at the hourly rate specified.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "web-cloud",
      title: "Web, Domain & Hosting Services",
      icon: <Globe className="w-5 h-5 text-brand-400" />,
      content: `Custom website development projects start at $2,000 (billed as a 50% deposit upon kickoff, and the remaining 50% final payment upon completion prior to live launch), which includes custom responsive layout design, SEO optimization, and standard lead routing forms. For domain purchases, we recommend Cloudflare. On the hosting side, we take care of all website edits at our hourly rate. If you need to edit your own site, additional charges apply based upon need. On hosting management: To ensure that you retain ownership of your files, we require that your site is hosted on your hosting account and that we are added as a delegated administrator on your account to ensure security. Regarding contact forms: initial volume beyond 250 submissions requires a small upgrade fee to increase submission limits. Our forms are hosted on a third-party provider to ensure security, data compliance, and to ensure that you own your submission data. The client retains 100% direct ownership of their domain registrars, DNS managers, and hosting accounts.`
    },
    {
      id: "privacy-confidentiality",
      title: "Privacy & Confidentiality",
      icon: <Lock className="w-5 h-5 text-brand-400" />,
      content: `Client privacy is strictly protected. Personal information submitted through online quote requests and website onboarding forms (including names, contact details, diagnostic notes, domain preferences, and digital signatures) is encrypted and securely stored in Google Cloud Firestore and transmitted via secure transactional email (Resend API) strictly for service fulfillment. We do not sell, rent, or share personal information with third-party advertisers. Equipment data and personal files will not be browsed, inspected, or disclosed beyond what is strictly necessary to complete diagnostics, repairs, or requested data transfers.`
    },
    {
      id: "hardware-risk",
      title: "Hardware Risk & Pre-Existing Defects",
      icon: <Cpu className="w-5 h-5 text-brand-400" />,
      content: `Due to component age, normal wear, thermal degradation, or pre-existing hardware defects, components (including mechanical drives, SSDs, capacitors, and system boards) may fail unpredictably during testing or diagnostics. Randon Brown, LLC assumes no liability for pre-existing defects or secondary component failure.`
    },
    {
      id: "parts-procurement",
      title: "Hardware & Parts Procurement",
      icon: <ShoppingCart className="w-5 h-5 text-brand-400" />,
      content: `To provide transparent pricing without retail markups, Randon Brown, LLC does not retail or inventory physical replacement parts. When upgrades or repairs require hardware components (such as storage drives, memory, or system boards), Randon Brown, LLC will identify compatible parts and provide direct purchase links for the client to order or provide verbal instruction. The client is solely responsible for purchasing the hardware, managing manufacturer warranties, and handling any product returns or exchanges. Minor consumables, installation materials, and diagnostic media used during service are incorporated into standard labor rates and carry no separate warranty. Randon Brown, LLC is not liable for manufacturing defects or shipping delays, and all labor required to test, configure, or install hardware constitutes a billable service.`
    },
    {
      id: "service-acceptance",
      title: "Service Acceptance & No Ongoing Warranty",
      icon: <Award className="w-5 h-5 text-brand-400" />,
      content: `All services are rendered on an incident-by-incident basis. Client acceptance of the equipment or payment of the invoice confirms that the requested work was inspected and completed to satisfaction. No ongoing warranty, guarantee of future performance, or complimentary follow-up support is provided. Any subsequent issues, user modifications, malware reinfections, or secondary hardware failures occurring after the equipment leaves the service provider’s possession constitute a new billable service. Hardware replacement parts carry only the manufacturer's warranty.`
    },
    {
      id: "limitation-liability",
      title: "Limitation of Liability",
      icon: <Scale className="w-5 h-5 text-brand-400" />,
      content: `To the maximum extent permitted by applicable law, Randon Brown, LLC shall not be liable for any indirect, incidental, special, or consequential damages—including lost profits, lost revenue, business interruption, or loss of productivity—arising from technical services, delays, or equipment downtime. Total aggregate liability for any and all claims, damages, or losses shall not exceed the total service fee charged on the final invoice.`
    },
    {
      id: "abandoned-property",
      title: "Abandoned Property & Storage",
      icon: <Clock className="w-5 h-5 text-brand-400" />,
      content: `Equipment not picked up or scheduled for return within 30 days following written notice of completion or diagnostic assessment (via text message, email, or physical mail) will be deemed legally abandoned. Abandoned equipment may be recycled, repurposed, or sold to satisfy outstanding labor, diagnostic, and storage balances.`
    }
  ];

  return (
    <div className="printable-modal-card space-y-8 max-w-4xl mx-auto px-3 sm:px-6 py-4">
      {/* Top Header Navigation */}
      <div className="flex items-center justify-between border-b border-neutral-900 pb-4 print-hide">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-neutral-900/80 hover:bg-neutral-800 text-slate-300 hover:text-white border border-neutral-800 hover:border-brand-500/40 text-xs sm:text-sm font-semibold transition-all duration-200 cursor-pointer group active:scale-95"
        >
          <ArrowLeft className="w-4 h-4 text-brand-400 group-hover:-translate-x-1 transition-transform" />
          <span>Back</span>
        </button>

        <div className="flex items-center gap-2 text-xs font-mono text-slate-400">
          <span className="hidden sm:inline">Policy /</span>
          <span className="text-brand-400 font-bold">Terms & Conditions</span>
        </div>
      </div>

      {/* Official Print Header (Visible ONLY when printing) */}
      <div className="hidden print:block print-header">
        <h1 className="print-header-title">Randon Brown, LLC | Terms &amp; Conditions</h1>
        <p className="print-header-sub">Operating Agreement &amp; Technical Services Policies • randonbrown.com</p>
        <p className="print-header-sub">Effective Date: October 2026 • Contact: help@randonbrown.com • (314) 944-3074 • Texas County, MO</p>
      </div>

      {/* Hero Welcome Header */}
      <div className="text-center max-w-2xl mx-auto space-y-2.5 print-hide">
        <div className="inline-flex items-center gap-2 py-1 px-3.5 rounded-full bg-brand-500/10 text-brand-400 text-xs font-mono font-bold uppercase tracking-wider border border-brand-500/20 select-none">
          <ShieldCheck className="w-3.5 h-3.5" />
          Operating Agreement & Service Policies
        </div>
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-display font-black text-white tracking-tight">
          Terms & Conditions
        </h1>
        <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
          Standard terms governing all on-site, pick-up, diagnostic, hardware repair, and web consulting services provided by Randon Brown, LLC.
        </p>
      </div>

      {/* Quick Summary Banner */}
      <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-neutral-950 via-brand-500/[0.08] to-neutral-950 border border-brand-500/30 flex items-start sm:items-center gap-3.5 text-left shadow-lg print-section-item">
        <div className="p-2.5 bg-brand-500/10 text-brand-400 rounded-xl shrink-0 border border-brand-500/20 print-hide">
          <FileText className="w-5 h-5" />
        </div>
        <div className="space-y-0.5 text-xs sm:text-sm text-slate-300 leading-relaxed print-text">
          <span className="font-bold text-white font-display block print-title">
            Clear, Transparent Client Protections
          </span>
          <p>
            Please review the following policies regarding payment protocols, client data backup responsibilities, hardware parts procurement, and service acceptance prior to authorizing work.
          </p>
        </div>
      </div>

      {/* Sections List */}
      <div className="space-y-4 text-left printable-modal-body">
        {sections.map((section, idx) => (
          <div
            key={section.id}
            className="print-section-item p-5 sm:p-6 rounded-2xl bg-neutral-950/90 border border-neutral-900 hover:border-brand-500/30 transition-all shadow-sm space-y-2.5"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-neutral-900 text-brand-400 border border-neutral-800 shrink-0 print-hide">
                {section.icon}
              </div>
              <div>
                <span className="text-[10px] font-mono text-brand-400 uppercase tracking-wider block font-bold">
                  Clause {idx + 1}
                </span>
                <h2 className="text-sm sm:text-base font-bold text-white font-display print-title">
                  {section.title}
                </h2>
              </div>
            </div>
            <div className="text-xs sm:text-sm text-slate-300 leading-relaxed pl-0 sm:pl-11 print-text">
              {section.content}
            </div>
          </div>
        ))}
      </div>

      {/* Bottom Footer Note & Contact Support */}
      <div className="p-6 rounded-3xl bg-neutral-950 border border-brand-500/20 text-center space-y-3 shadow-lg print-hide">
        <h3 className="text-sm sm:text-base font-bold text-white font-display">
          Have Questions About Our Service Terms?
        </h3>
        <p className="text-xs text-slate-400 max-w-xl mx-auto leading-relaxed">
          We believe in direct, transparent communication. If you have any questions regarding your invoice, diagnostic findings, or parts ordering, contact us directly at{' '}
          <a href="mailto:help@randonbrown.com" className="text-brand-400 underline font-semibold hover:text-brand-300">
            help@randonbrown.com
          </a>.
        </p>
        <div className="pt-2 flex justify-center">
          <button
            onClick={onBack}
            className="px-6 py-2.5 rounded-xl bg-brand-500 hover:bg-brand-400 text-neutral-950 text-xs font-bold font-mono uppercase tracking-wider cursor-pointer transition-all active:scale-95 shadow-md"
          >
            Back to Application
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
